import { gql, useMutation, useQuery } from '@apollo/client';
import AddBankAccountModal from '@components/modals/addBankAccountModal';
import ConfirmationModal from '@components/modals/confirmationModal';
import type { Account } from '@interfaces/account';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { usePermissions } from '@providers/userContextProvider';
import { colors } from '@theme/colors';
import {
  filterBankAccountsByCurrency,
  generateBankDetailString,
} from '@utils/common';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FETCH_BANK_ACCOUNTS = gql`
  query fetchBankAccounts($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        bankAccounts {
          id
          name
          bankAccountNumber
          institutionNumber
          currency
        }
      }
    }
  }
`;

const DELETE_BANK_ACCOUNT = gql`
  mutation deleteBankAccount($bankAccountId: ObjectID!) {
    deleteBankAccount(bankAccountId: $bankAccountId) {
      bankAccount {
        id
        name
      }
    }
  }
`;

type BankAccountSelectProps = {
  userId: string;
  setBankAccount: (event: string) => void;
  value: string;
  label: string;
  size?: 'small' | 'medium' | undefined;
  disabled?: boolean;
  readonly?: boolean;
  canEditBankAccount?: boolean;
  account?: Account | undefined;
};

export const BankAccountSelect = ({
  userId,
  setBankAccount,
  value,
  label,
  size,
  disabled = false,
  readonly = false,
  canEditBankAccount = true,
  account = undefined,
}: BankAccountSelectProps) => {
  const { t } = useTranslation(['components']);
  const { permissions } = usePermissions();
  const [addBankAccount, setAddBankAccount] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const selectedBankAccountId = useRef<string>('');
  const { data, loading, refetch } = useQuery(FETCH_BANK_ACCOUNTS, {
    skip: userId === '' || !permissions.includes('read:bank_account_basic'),
    variables: { userId },
  });
  const bankAccounts = useMemo(() => {
    const list = data?.fetchUser?.user?.bankAccounts;
    if (!list) return undefined;
    return filterBankAccountsByCurrency(list, account);
  }, [data, account]);

  const [deleteBankAccountMutation] = useMutation(DELETE_BANK_ACCOUNT, {
    variables: { bankAccountId: selectedBankAccountId.current },
    onCompleted: () => refetch(),
  });

  const deleteBankAccount = () => {
    deleteBankAccountMutation().then();
    setOpenConfirmationModal(false);
  };

  if (!permissions.includes('read:bank_account_basic')) return null;

  return (
    <>
      <FormControl fullWidth>
        <InputLabel id='choose-bank'>{label}</InputLabel>
        <Select
          value={bankAccounts ? value : ''}
          labelId='choose-bank'
          data-testid='bankAccount-dropdown'
          input={<OutlinedInput label={label} />}
          fullWidth
          onChange={(e) => {
            if (e.target.value === 'addBank') {
              setAddBankAccount(true);
            } else {
              setAddBankAccount(false);
              setBankAccount(e.target.value);
            }
          }}
          size={size}
          disabled={disabled}
          inputProps={{ readOnly: readonly }}
          renderValue={(selected) => {
            const foundBankAccount = bankAccounts?.find(
              (item: Record<string, unknown>) => item.id === selected,
            );
            if (foundBankAccount) {
              return generateBankDetailString(foundBankAccount);
            }
            return '';
          }}
        >
          {loading ? (
            <MenuItem>...</MenuItem>
          ) : (
            (bankAccounts || [])?.map((x: any) => (
              <MenuItem
                onClick={() => setBankAccount(x.id)}
                key={x.id}
                value={x.id}
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                {x.name} {x.bankAccountNumber}
                {canEditBankAccount &&
                permissions.includes('write:bank_account_basic') ? (
                  <IconButton
                    aria-label='comment'
                    onClick={() => {
                      selectedBankAccountId.current = x.id;
                      setOpenConfirmationModal(true);
                    }}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                ) : undefined}
              </MenuItem>
            ))
          )}
          {canEditBankAccount &&
            permissions.includes('write:bank_account_basic') && (
              <MenuItem
                sx={{ color: colors.success }}
                key='addBank'
                value='addBank'
              >
                {t('components:bankAccountSelect.addBank')}
              </MenuItem>
            )}
        </Select>
      </FormControl>
      {canEditBankAccount && (
        <AddBankAccountModal
          handleClose={() => setAddBankAccount(false)}
          open={addBankAccount}
          afterCreate={(bank) => {
            setBankAccount(bank.id);
            refetch();
            setAddBankAccount(false);
          }}
          userId={userId}
        />
      )}
      <ConfirmationModal
        open={openConfirmationModal}
        title={t('components:bankAccountSelect.confirmationDialogTitle')}
        onCancel={() => setOpenConfirmationModal(false)}
        onConfirm={() => deleteBankAccount()}
      />
    </>
  );
};

export const testables = {
  FETCH_BANK_ACCOUNTS,
};
